import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { adminApi } from '../api/admin.js';
import { ApiError } from '../api/client.js';
import { Avatar } from '../components/Avatar.jsx';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { AuthenticatedImage } from '../components/AuthenticatedImage.jsx';
import { ScreenshotLightbox } from '../components/ScreenshotLightbox.jsx';
import { utcStringToOffice, formatHourMinute } from '../lib/time.js';

export default function AdminEmployeeDetailPage() {
    const { id } = useParams();
    const queryClient = useQueryClient();

    const { data, isLoading, isError, error: queryError } = useQuery({
        queryKey: ['admin', 'employee', id],
        queryFn: () => adminApi.getEmployee(id),
    });

    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [editError, setEditError] = useState(null);
    const [editFieldErrors, setEditFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [actionMsg, setActionMsg] = useState(null);
    const [confirm, setConfirm] = useState({ open: false });
    const [tempPassword, setTempPassword] = useState(null);
    const [shiftModal, setShiftModal] = useState({ open: false });

    const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin', 'employee', id] });

    const startEdit = () => {
        setEditForm({
            name: data.employee.name,
            email: data.employee.email,
            department: data.employee.department,
            role: data.employee.role,
        });
        setEditing(true);
        setEditError(null);
        setEditFieldErrors({});
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setEditError(null);
        setEditFieldErrors({});
        try {
            await adminApi.updateEmployee(id, editForm);
            setEditing(false);
            setActionMsg({ tone: 'success', text: 'Employee updated.' });
            refresh();
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === 'EMAIL_TAKEN') setEditFieldErrors({ email: 'Email already in use.' });
                else if (err.code === 'CONFLICT') setEditError(err.message);
                else setEditError(err.message);
            } else {
                setEditError('Could not save changes.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeactivate = () => setConfirm({
        open: true,
        title: 'Deactivate this employee?',
        body: `${data.employee.name} will no longer be able to log in. Their attendance history will be preserved. You can reactivate at any time.`,
        confirmLabel: 'Deactivate',
        confirmTone: 'danger',
        action: async () => {
            await adminApi.updateEmployee(id, { is_active: false });
            setActionMsg({ tone: 'success', text: 'Employee deactivated.' });
            refresh();
        },
    });

    const handleReactivate = () => setConfirm({
        open: true,
        title: 'Reactivate this employee?',
        body: `${data.employee.name} will be able to log in again with their existing password.`,
        confirmLabel: 'Reactivate',
        confirmTone: 'primary',
        action: async () => {
            await adminApi.updateEmployee(id, { is_active: true });
            setActionMsg({ tone: 'success', text: 'Employee reactivated.' });
            refresh();
        },
    });

    const handleResetPassword = () => setConfirm({
        open: true,
        title: 'Reset password?',
        body: `A temporary password will be generated for ${data.employee.name}. They will be required to change it on next login. Their existing sessions will be revoked.`,
        confirmLabel: 'Reset password',
        confirmTone: 'danger',
        action: async () => {
            const result = await adminApi.resetPassword(id);
            setTempPassword(result.temporary_password);
            refresh();
        },
    });

    const handleForceLogout = () => setConfirm({
        open: true,
        title: 'Force logout?',
        body: `All active sessions for ${data.employee.name} will be revoked. They will need to log in again on every device.`,
        confirmLabel: 'Force logout',
        confirmTone: 'danger',
        action: async () => {
            await adminApi.forceLogout(id);
            setActionMsg({ tone: 'success', text: 'All sessions revoked.' });
        },
    });

    const handleUnlock = () => setConfirm({
        open: true,
        title: 'Unlock account?',
        body: `${data.employee.name}'s lockout will be cleared and they can attempt to log in immediately.`,
        confirmLabel: 'Unlock',
        confirmTone: 'primary',
        action: async () => {
            await adminApi.unlock(id);
            setActionMsg({ tone: 'success', text: 'Account unlocked.' });
            refresh();
        },
    });

    const performConfirm = async () => {
        const action = confirm.action;
        setConfirm({ open: false });
        try {
            await action();
        } catch (err) {
            setActionMsg({ tone: 'error', text: err.message || 'Action failed.' });
        }
    };

    if (isLoading) return <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading…</p>;
    if (isError) {
        if (queryError?.code === 'NOT_FOUND') {
            return (
                <div>
                    <p style={{ fontSize: 13, color: 'var(--color-danger)', marginBottom: 12 }}>Employee not found.</p>
                    <Link to="/admin/employees" style={{ fontSize: 13, color: 'var(--color-text-info)' }}>
                        ← Back to employees
                    </Link>
                </div>
            );
        }
        return <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>Could not load employee.</p>;
    }

    const emp = data.employee;
    const lockedNow = emp.locked_until && new Date(emp.locked_until) > new Date();

    return (
        <>
            <div className="flex items-center" style={{ gap: 8, fontSize: 13, marginBottom: 14 }}>
                <Link to="/admin/employees" style={{ color: 'var(--color-text-tertiary)', textDecoration: 'none' }}>
                    Employees
                </Link>
                <span style={{ color: 'var(--color-text-tertiary)' }}>/</span>
                <span>{emp.name}</span>
            </div>

            <div className="flex items-start" style={{ gap: 16, marginBottom: 18 }}>
                <Avatar user={emp} size="lg" />
                <div className="flex-1">
                    <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>{emp.name}</h1>
                        {!emp.is_active && <span className="pill pill-cancelled">Inactive</span>}
                        {lockedNow && (
                            <span className="pill pill-rejected">
                                🔒 Locked until {new Date(emp.locked_until).toLocaleString()}
                            </span>
                        )}
                        {emp.must_change_password && (
                            <span className="pill pill-pending">Pwd reset pending</span>
                        )}
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        {emp.email} · {emp.role} · {emp.department}
                    </p>
                </div>
                {!editing && emp.is_active && (
                    <button onClick={startEdit} className="btn btn-soft">Edit profile</button>
                )}
            </div>

            {actionMsg && (
                <div
                    style={{
                        marginBottom: 14,
                        padding: '8px 12px',
                        fontSize: 13,
                        borderRadius: 'var(--border-radius-md)',
                        ...(actionMsg.tone === 'success'
                            ? {
                                background: 'var(--color-pill-approved-bg)',
                                color: 'var(--color-pill-approved-fg)',
                                border: '0.5px solid rgba(8, 80, 65, 0.18)',
                            }
                            : {
                                background: 'var(--color-pill-rejected-bg)',
                                color: 'var(--color-danger)',
                                border: '0.5px solid var(--color-danger-border)',
                            }),
                    }}
                >
                    {actionMsg.text}
                    <button
                        onClick={() => setActionMsg(null)}
                        style={{
                            marginLeft: 12,
                            fontSize: 11,
                            textDecoration: 'underline',
                            color: 'inherit',
                            background: 'transparent',
                            cursor: 'pointer',
                            border: 'none',
                        }}
                    >
                        dismiss
                    </button>
                </div>
            )}

            {tempPassword && (
                <div
                    style={{
                        marginBottom: 14,
                        padding: 14,
                        background: 'var(--color-pill-pending-bg)',
                        border: '0.5px solid var(--color-status-incomplete)',
                        borderRadius: 'var(--border-radius-md)',
                    }}
                >
                    <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-pill-pending-fg)', marginBottom: 8 }}>
                        ⚠️ Temporary password — copy now, this will not be shown again
                    </p>
                    <code
                        style={{
                            display: 'block',
                            background: 'var(--color-bg-primary)',
                            padding: '10px 12px',
                            borderRadius: 'var(--border-radius-sm)',
                            border: '0.5px solid var(--color-border-tertiary)',
                            fontSize: 14,
                            fontVariantNumeric: 'tabular-nums',
                            userSelect: 'all',
                        }}
                    >
                        {tempPassword}
                    </code>
                    <p style={{ fontSize: 12, color: 'var(--color-pill-pending-fg)', marginTop: 8 }}>
                        Send this to {emp.name} via Slack or in person. They must change it on next login.
                    </p>
                    <button
                        onClick={() => setTempPassword(null)}
                        style={{
                            marginTop: 8,
                            fontSize: 11,
                            textDecoration: 'underline',
                            background: 'transparent',
                            color: 'var(--color-pill-pending-fg)',
                            cursor: 'pointer',
                            border: 'none',
                        }}
                    >
                        I have saved it — dismiss
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1" style={{ gap: 14, gridTemplateColumns: '2fr 1fr', marginBottom: 18 }}>
                {editing ? (
                    <form
                        onSubmit={saveEdit}
                        className="surface-card flex flex-col"
                        style={{ padding: 18, gap: 12 }}
                    >
                        <h2 style={{ fontSize: 14, fontWeight: 500, margin: 0, color: 'var(--color-text-secondary)' }}>
                            Edit profile
                        </h2>
                        <EditField label="Name">
                            <input
                                type="text"
                                value={editForm.name}
                                onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))}
                                className="input"
                            />
                        </EditField>
                        <EditField label="Email" error={editFieldErrors.email}>
                            <input
                                type="email"
                                value={editForm.email}
                                onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))}
                                className="input"
                            />
                        </EditField>
                        <EditField label="Department">
                            <input
                                type="text"
                                value={editForm.department}
                                onChange={(e) => setEditForm((s) => ({ ...s, department: e.target.value }))}
                                className="input"
                            />
                        </EditField>
                        <EditField label="Role">
                            <select
                                value={editForm.role}
                                onChange={(e) => setEditForm((s) => ({ ...s, role: e.target.value }))}
                                className="select"
                            >
                                <option value="employee">Employee</option>
                                <option value="manager">Manager</option>
                                <option value="admin">Admin</option>
                            </select>
                        </EditField>
                        {editError && (
                            <p
                                style={{
                                    fontSize: 13,
                                    color: 'var(--color-danger)',
                                    background: 'var(--color-pill-rejected-bg)',
                                    border: '0.5px solid var(--color-danger-border)',
                                    borderRadius: 'var(--border-radius-md)',
                                    padding: '8px 12px',
                                }}
                            >
                                {editError}
                            </p>
                        )}
                        <div className="flex justify-end" style={{ gap: 8, paddingTop: 6 }}>
                            <button type="button" onClick={() => setEditing(false)} className="btn btn-ghost">
                                Cancel
                            </button>
                            <button type="submit" disabled={submitting} className="btn btn-primary">
                                {submitting ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="surface-card" style={{ padding: 18 }}>
                        <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 10, color: 'var(--color-text-secondary)' }}>
                            Details
                        </h2>
                        <DetailRow label="Date of joining" value={emp.date_of_joining} />
                        <DetailRow
                            label="Created"
                            value={emp.created_at ? new Date(emp.created_at).toLocaleString() : '—'}
                        />

                        <h2 style={{ fontSize: 14, fontWeight: 500, marginTop: 18, marginBottom: 10, color: 'var(--color-text-secondary)' }}>
                            Current shift
                        </h2>
                        {data.current_shift ? (
                            <>
                                <DetailRow
                                    label="Hours"
                                    value={`${formatTime(data.current_shift.shift_start_time)} – ${formatTime(data.current_shift.shift_end_time)}`}
                                />
                                <DetailRow label="Effective from" value={formatDate(data.current_shift.effective_from)} />
                            </>
                        ) : (
                            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                                No active shift configured.
                            </p>
                        )}
                        <PendingShiftBanner shiftHistory={data.shift_history} />
                        {emp.is_active && (
                            <button
                                onClick={() => setShiftModal({ open: true })}
                                className="btn btn-soft"
                                style={{ marginTop: 10, fontSize: 12, padding: '6px 12px' }}
                            >
                                {data.current_shift ? 'Change shift' : 'Set shift'}
                            </button>
                        )}

                        <h2 style={{ fontSize: 14, fontWeight: 500, marginTop: 18, marginBottom: 10, color: 'var(--color-text-secondary)' }}>
                            Leave balance
                        </h2>
                        {data.leave_balance ? (
                            <>
                                <DetailRow label="Year" value={`Starting ${formatDate(data.leave_balance.leave_year_start)}`} />
                                <DetailRow label="Allocated" value={`${data.leave_balance.allocated_days} days`} />
                                <DetailRow label="Used" value={`${data.leave_balance.used_days} days`} />
                                <DetailRow label="Remaining" value={`${data.leave_balance.remaining_days} days`} highlight />
                            </>
                        ) : (
                            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                                No leave balance for the current year.
                            </p>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="surface-card" style={{ padding: 18 }}>
                    <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 10, color: 'var(--color-text-secondary)' }}>
                        Actions
                    </h2>
                    <div className="flex flex-col" style={{ gap: 8 }}>
                        {emp.is_active && (
                            <ActionButton onClick={handleResetPassword}>Reset password</ActionButton>
                        )}
                        {emp.is_active && (
                            <ActionButton onClick={handleForceLogout}>Force log out</ActionButton>
                        )}
                        {lockedNow && (
                            <ActionButton onClick={handleUnlock} tone="primary">
                                Unlock account
                            </ActionButton>
                        )}
                        {emp.is_active ? (
                            <ActionButton onClick={handleDeactivate} tone="danger">
                                Deactivate
                            </ActionButton>
                        ) : (
                            <ActionButton onClick={handleReactivate} tone="primary">
                                Reactivate
                            </ActionButton>
                        )}
                    </div>
                </div>
            </div>

            {emp.role === 'manager' && (
                <DepartmentAccessSection
                    employeeId={id}
                    initialDepartments={(data.manager_departments || []).map((d) => d.department)}
                    onSaved={refresh}
                />
            )}

            <AttendanceSection employeeId={id} />

            <ScreenshotsSection employeeId={id} />

            <ConfirmDialog
                open={confirm.open}
                title={confirm.title}
                body={confirm.body}
                confirmLabel={confirm.confirmLabel}
                confirmTone={confirm.confirmTone}
                onConfirm={performConfirm}
                onCancel={() => setConfirm({ open: false })}
            />

            {shiftModal.open && (
                <ChangeShiftModal
                    employee={emp}
                    currentShift={data.current_shift}
                    onCancel={() => setShiftModal({ open: false })}
                    onSuccess={(newShift) => {
                        setShiftModal({ open: false });
                        setActionMsg({
                            tone: 'success',
                            text: `Shift updated. New hours ${formatTime(newShift.shift_start_time)}–${formatTime(newShift.shift_end_time)} take effect on ${formatDate(newShift.effective_from)}.`,
                        });
                        refresh();
                    }}
                />
            )}
        </>
    );
}

function DetailRow({ label, value, highlight }) {
    return (
        <div
            className="flex justify-between"
            style={{
                fontSize: 13,
                padding: '6px 0',
                borderBottom: '0.5px solid var(--color-border-tertiary)',
            }}
        >
            <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
            <span style={{
                fontWeight: highlight ? 500 : 400,
                color: highlight ? 'var(--color-text-primary)' : 'var(--color-text-primary)',
            }}>
                {value}
            </span>
        </div>
    );
}

function ActionButton({ onClick, tone, children }) {
    let style = {
        background: 'var(--color-bg-secondary)',
        color: 'var(--color-text-primary)',
        border: '0.5px solid var(--color-border-tertiary)',
    };
    if (tone === 'primary') {
        style = {
            background: 'var(--color-pill-approved-bg)',
            color: 'var(--color-pill-approved-fg)',
            border: '0.5px solid rgba(8, 80, 65, 0.18)',
        };
    } else if (tone === 'danger') {
        style = {
            background: 'var(--color-pill-rejected-bg)',
            color: 'var(--color-danger)',
            border: '0.5px solid var(--color-danger-border)',
        };
    }
    return (
        <button
            onClick={onClick}
            style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 13,
                borderRadius: 'var(--border-radius-md)',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'opacity 120ms ease',
                ...style,
            }}
        >
            {children}
        </button>
    );
}

function EditField({ label, error, children }) {
    return (
        <div>
            <label
                style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 4,
                }}
            >
                {label}
            </label>
            {children}
            {error && (
                <p style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 4 }}>{error}</p>
            )}
        </div>
    );
}

function formatTime(hms) {
    if (!hms) return '';
    const [h, m] = hms.split(':');
    const hour = Number(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
}

function formatDate(s) {
    if (!s) return '—';
    const d = s instanceof Date ? s : new Date(s);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------

function PendingShiftBanner({ shiftHistory }) {
    if (!shiftHistory || shiftHistory.length === 0) return null;
    // Find a future-dated shift (effective_from > today)
    const today = new Date().toISOString().slice(0, 10);
    const future = shiftHistory.find((s) => {
        const ef = s.effective_from instanceof Date
            ? s.effective_from.toISOString().slice(0, 10)
            : String(s.effective_from).slice(0, 10);
        return ef > today;
    });
    if (!future) return null;
    return (
        <div
            style={{
                marginTop: 8,
                padding: '8px 10px',
                background: 'var(--color-bg-info)',
                border: '0.5px solid var(--color-border-info)',
                borderRadius: 'var(--border-radius-md)',
                fontSize: 12,
                color: 'var(--color-text-info)',
                lineHeight: 1.5,
            }}
        >
            <strong>Pending change:</strong>{' '}
            New shift {formatTime(future.shift_start_time)}–{formatTime(future.shift_end_time)} starts on{' '}
            {formatDate(future.effective_from)}.
        </div>
    );
}

function ChangeShiftModal({ employee, currentShift, onCancel, onSuccess }) {
    const initialStart = currentShift ? trimSeconds(currentShift.shift_start_time) : '10:00';
    const initialEnd = currentShift ? trimSeconds(currentShift.shift_end_time) : '19:00';

    const [start, setStart] = useState(initialStart);
    const [end, setEnd] = useState(initialEnd);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (start >= end) {
            setError('Start time must be before end time.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await adminApi.updateShift(employee.id, {
                shift_start_time: start,
                shift_end_time: end,
            });
            onSuccess(result.new_shift);
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('Could not update shift.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                zIndex: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <form
                onSubmit={onSubmit}
                className="surface-card"
                style={{ padding: 22, width: '100%', maxWidth: 460 }}
            >
                <h2 style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>
                    {currentShift ? 'Change shift' : 'Set shift'} for {employee.name}
                </h2>
                <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
                    The new shift takes effect <strong>tomorrow</strong>. Today's hours and any open
                    session continue under the current shift.
                </p>

                <div className="grid" style={{ gap: 10, gridTemplateColumns: '1fr 1fr', marginBottom: 14 }}>
                    <div>
                        <label
                            style={{
                                display: 'block', fontSize: 12, fontWeight: 500,
                                color: 'var(--color-text-secondary)', marginBottom: 4,
                            }}
                        >
                            Shift start
                        </label>
                        <input
                            type="time"
                            required
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                            className="input"
                        />
                    </div>
                    <div>
                        <label
                            style={{
                                display: 'block', fontSize: 12, fontWeight: 500,
                                color: 'var(--color-text-secondary)', marginBottom: 4,
                            }}
                        >
                            Shift end
                        </label>
                        <input
                            type="time"
                            required
                            value={end}
                            onChange={(e) => setEnd(e.target.value)}
                            className="input"
                        />
                    </div>
                </div>

                {error && (
                    <p
                        style={{
                            fontSize: 13,
                            color: 'var(--color-danger)',
                            background: 'var(--color-pill-rejected-bg)',
                            border: '0.5px solid var(--color-danger-border)',
                            borderRadius: 'var(--border-radius-md)',
                            padding: '8px 12px',
                            marginBottom: 12,
                        }}
                    >
                        {error}
                    </p>
                )}

                <div className="flex justify-end" style={{ gap: 8, paddingTop: 8, borderTop: '0.5px solid var(--color-border-tertiary)' }}>
                    <button type="button" onClick={onCancel} className="btn btn-ghost">Cancel</button>
                    <button type="submit" disabled={submitting} className="btn btn-primary">
                        {submitting ? 'Saving…' : 'Save shift'}
                    </button>
                </div>
            </form>
        </div>
    );
}

function trimSeconds(t) {
    if (!t) return '';
    return t.length > 5 ? t.slice(0, 5) : t;
}

// ---------------------------------------------------------------------------
// Attendance section — inline view of an employee's day-by-day attendance.
// Uses /api/v1/admin/employees/:id/attendance.
// ---------------------------------------------------------------------------

const RANGE_PRESETS = [
    { key: 'today',      label: 'Today' },
    { key: 'this_week',  label: 'This week' },
    { key: 'this_month', label: 'This month' },
    { key: 'last_30',    label: 'Last 30 days' },
    { key: 'custom',     label: 'Custom' },
];

function computeRange(preset, customStart, customEnd) {
    const today = DateTime.now().setZone('Asia/Karachi').startOf('day');
    if (preset === 'today') {
        const s = today.toISODate();
        return { start: s, end: s };
    }
    if (preset === 'this_week') {
        const monday = today.startOf('week');
        const sunday = monday.plus({ days: 6 });
        return { start: monday.toISODate(), end: sunday.toISODate() };
    }
    if (preset === 'this_month') {
        return { start: today.startOf('month').toISODate(), end: today.toISODate() };
    }
    if (preset === 'last_30') {
        return { start: today.minus({ days: 29 }).toISODate(), end: today.toISODate() };
    }
    if (preset === 'custom' && customStart && customEnd) {
        return { start: customStart, end: customEnd };
    }
    return { start: null, end: null };
}

function AttendanceSection({ employeeId }) {
    const [preset, setPreset] = useState('this_week');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [customError, setCustomError] = useState(null);
    const [expanded, setExpanded] = useState(() => new Set());

    const { start, end } = useMemo(
        () => computeRange(preset, customStart, customEnd),
        [preset, customStart, customEnd]
    );

    const query = useQuery({
        queryKey: ['admin', 'employee', employeeId, 'attendance', start, end],
        queryFn: () => adminApi.getEmployeeAttendance(employeeId, { start, end }),
        enabled: !!start && !!end,
    });

    const toggle = (date) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(date)) next.delete(date);
            else next.add(date);
            return next;
        });
    };

    const applyCustom = () => {
        if (!customStart || !customEnd) {
            setCustomError('Both dates are required.');
            return;
        }
        if (customEnd < customStart) {
            setCustomError('End must be on or after start.');
            return;
        }
        const days = Math.round(
            DateTime.fromISO(customEnd).diff(DateTime.fromISO(customStart), 'days').days
        ) + 1;
        if (days > 365) {
            setCustomError('Range cannot exceed 365 days.');
            return;
        }
        setCustomError(null);
        setPreset('custom');
    };

    return (
        <div className="surface-card" style={{ padding: 18, marginBottom: 18 }}>
            <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: 'var(--color-text-secondary)' }}>
                Attendance
            </h2>

            <div className="flex items-center" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {RANGE_PRESETS.map((p) => (
                    <RangeChip
                        key={p.key}
                        active={preset === p.key}
                        onClick={() => {
                            setPreset(p.key);
                            setCustomError(null);
                        }}
                    >
                        {p.label}
                    </RangeChip>
                ))}
            </div>

            {preset === 'custom' && (
                <div className="flex items-end" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div>
                        <label
                            style={{
                                display: 'block', fontSize: 12, fontWeight: 500,
                                color: 'var(--color-text-secondary)', marginBottom: 4,
                            }}
                        >
                            Start
                        </label>
                        <input
                            type="date"
                            value={customStart}
                            onChange={(e) => setCustomStart(e.target.value)}
                            className="input"
                            style={{ width: 160 }}
                        />
                    </div>
                    <div>
                        <label
                            style={{
                                display: 'block', fontSize: 12, fontWeight: 500,
                                color: 'var(--color-text-secondary)', marginBottom: 4,
                            }}
                        >
                            End
                        </label>
                        <input
                            type="date"
                            value={customEnd}
                            onChange={(e) => setCustomEnd(e.target.value)}
                            className="input"
                            style={{ width: 160 }}
                        />
                    </div>
                    <button onClick={applyCustom} className="btn btn-primary">Go</button>
                </div>
            )}

            {customError && (
                <p style={{ fontSize: 13, color: 'var(--color-danger)', marginBottom: 10 }}>
                    {customError}
                </p>
            )}

            {query.isLoading && (
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading…</p>
            )}
            {query.isError && (
                <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>
                    Could not load attendance.
                </p>
            )}
            {query.isSuccess && (!query.data?.days || query.data.days.length === 0) && (
                <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                    No attendance data in this range.
                </p>
            )}

            {query.isSuccess && query.data?.days?.length > 0 && (
                <div className="flex flex-col" style={{ gap: 4 }}>
                    {query.data.days.map((d) => (
                        <DayRow
                            key={d.date}
                            day={d}
                            expanded={expanded.has(d.date)}
                            onToggle={() => toggle(d.date)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function RangeChip({ active, onClick, children }) {
    return (
        <button
            onClick={onClick}
            style={{
                padding: '5px 11px',
                fontSize: 12,
                fontWeight: 500,
                borderRadius: 'var(--border-radius-md)',
                cursor: 'pointer',
                border: '0.5px solid var(--color-border-tertiary)',
                background: active ? 'var(--color-bg-info)' : 'var(--color-bg-primary)',
                color: active ? 'var(--color-text-info)' : 'var(--color-text-secondary)',
                borderColor: active ? 'var(--color-border-info)' : 'var(--color-border-tertiary)',
                transition: 'background-color 120ms ease, border-color 120ms ease',
            }}
        >
            {children}
        </button>
    );
}

// Resolves a day's display kind by the priority rules:
//   weekend (no sessions) → holiday (no sessions) → full-day leave (no sessions)
//   → half-day leave with sessions → in-progress (today + open session)
//   → complete / incomplete / on_leave (attendance row exists)
//   → absent (past working day, no data) → today_no_data (today, no row yet)
function classifyDay(day) {
    const sessions = day.attendance?.sessions || [];
    const hasOpenSession = sessions.some((s) => s.check_out_time === null);

    if (day.is_weekend && sessions.length === 0) {
        return { kind: 'weekend', label: 'Weekend', expandable: false };
    }
    if (day.holiday && sessions.length === 0) {
        return { kind: 'holiday', label: `Holiday · ${day.holiday.name}`, expandable: false };
    }
    if (day.leave && !day.leave.is_half_day && sessions.length === 0) {
        return {
            kind: 'on_leave',
            label: `On leave · ${capitalize(day.leave.type)}`,
            expandable: true,
        };
    }
    if (day.leave && day.leave.is_half_day && sessions.length > 0) {
        return {
            kind: 'half_day_with_work',
            label: `Half-day leave · ${capitalize(day.leave.type)} (${day.leave.half_day_period})`,
            expandable: true,
        };
    }
    if (day.attendance) {
        if (day.is_today && hasOpenSession) {
            return { kind: 'in_progress', label: 'In progress', expandable: true };
        }
        if (day.attendance.status === 'complete') {
            return { kind: 'complete', label: 'Complete', expandable: true };
        }
        if (day.attendance.status === 'incomplete') {
            return { kind: 'incomplete', label: 'Incomplete', expandable: true };
        }
        if (day.attendance.status === 'on_leave') {
            return {
                kind: 'on_leave',
                label: day.leave ? `On leave · ${capitalize(day.leave.type)}` : 'On leave',
                expandable: true,
            };
        }
        // Unknown status — show it as-is, expandable.
        return { kind: 'other', label: capitalize(day.attendance.status), expandable: true };
    }
    if (day.is_today) {
        return { kind: 'today_no_data', label: 'No check-in yet', expandable: false };
    }
    return { kind: 'absent', label: 'Absent', expandable: false };
}

function kindStyle(kind) {
    // Background tint + pill class for the status pill on the collapsed row.
    switch (kind) {
        case 'weekend':
            return { bg: 'var(--color-bg-secondary)', pill: 'pill-cancelled', faded: true };
        case 'holiday':
            return { bg: 'var(--color-bg-secondary)', pill: 'pill-cancelled', faded: true };
        case 'on_leave':
        case 'half_day_with_work':
            return { bg: 'var(--color-bg-primary)', pill: 'pill-on-leave', faded: false };
        case 'in_progress':
            return { bg: 'var(--color-bg-info)', pill: 'pill-in-progress', faded: false };
        case 'complete':
            return { bg: 'var(--color-bg-primary)', pill: 'pill-approved', faded: false };
        case 'incomplete':
            return { bg: 'var(--color-bg-primary)', pill: 'pill-pending', faded: false };
        case 'absent':
            return { bg: 'var(--color-bg-primary)', pill: 'pill-rejected', faded: false };
        case 'today_no_data':
            return { bg: 'var(--color-bg-info)', pill: 'pill-in-progress', faded: false };
        default:
            return { bg: 'var(--color-bg-primary)', pill: 'pill-cancelled', faded: false };
    }
}

function DayRow({ day, expanded, onToggle }) {
    const cls = classifyDay(day);
    const style = kindStyle(cls.kind);
    const dt = DateTime.fromISO(day.date);
    const dateLabel = `${day.weekday} ${dt.toFormat('d LLL')}`;
    const sessions = day.attendance?.sessions || [];
    const totalMinutes = day.attendance ? Number(day.attendance.total_hours) * 60 : null;
    const showHours = totalMinutes !== null && cls.kind !== 'weekend' && cls.kind !== 'holiday';

    return (
        <div
            style={{
                background: style.bg,
                border: '0.5px solid var(--color-border-tertiary)',
                borderRadius: 'var(--border-radius-md)',
                opacity: style.faded ? 0.85 : 1,
            }}
        >
            <button
                onClick={cls.expandable ? onToggle : undefined}
                disabled={!cls.expandable}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 12px',
                    background: 'transparent',
                    border: 'none',
                    cursor: cls.expandable ? 'pointer' : 'default',
                    textAlign: 'left',
                }}
            >
                <span
                    style={{
                        fontSize: 12,
                        fontVariantNumeric: 'tabular-nums',
                        color: 'var(--color-text-secondary)',
                        width: 76,
                        flexShrink: 0,
                    }}
                >
                    {dateLabel}
                    {day.is_today && (
                        <span style={{ marginLeft: 4, color: 'var(--color-status-today-deep)', fontWeight: 500 }}>·</span>
                    )}
                </span>
                <PillBadge kind={cls.kind}>{cls.label}</PillBadge>
                <span style={{ flex: 1 }} />
                {showHours && (
                    <span
                        className="tnum"
                        style={{
                            fontSize: 13,
                            color: 'var(--color-text-primary)',
                            fontWeight: 500,
                        }}
                    >
                        {formatHourMinute(totalMinutes)}
                    </span>
                )}
                {!showHours && (
                    <span style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>—</span>
                )}
                {cls.expandable && (
                    <span
                        style={{
                            fontSize: 11,
                            color: 'var(--color-text-tertiary)',
                            width: 12,
                            textAlign: 'center',
                        }}
                    >
                        {expanded ? '▾' : '▸'}
                    </span>
                )}
            </button>

            {cls.expandable && expanded && (
                <div
                    style={{
                        padding: '8px 14px 12px 14px',
                        borderTop: '0.5px solid var(--color-border-tertiary)',
                    }}
                >
                    {sessions.length === 0 && day.leave && (
                        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                            Leave request <code style={{ fontSize: 11 }}>{day.leave.request_id}</code>
                        </p>
                    )}
                    {sessions.length > 0 && (
                        <div className="flex flex-col" style={{ gap: 6 }}>
                            {sessions.map((s) => (
                                <SessionRow key={s.id} session={s} />
                            ))}
                        </div>
                    )}
                    {day.attendance?.check_out_note && (
                        <p
                            style={{
                                marginTop: 8,
                                fontSize: 12,
                                fontStyle: 'italic',
                                color: 'var(--color-text-secondary)',
                            }}
                        >
                            “{day.attendance.check_out_note}”
                        </p>
                    )}
                    {day.leave && day.leave.is_half_day && sessions.length > 0 && (
                        <p style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                            <span className="pill pill-on-leave" style={{ marginRight: 6 }}>
                                {day.leave.half_day_period} on leave
                            </span>
                            {capitalize(day.leave.type)} leave for the {day.leave.half_day_period}.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

function SessionRow({ session }) {
    const inTime = utcStringToOffice(session.check_in_time).toFormat('HH:mm');
    const outTime = session.check_out_time
        ? utcStringToOffice(session.check_out_time).toFormat('HH:mm')
        : null;
    const duration = session.duration_minutes !== null
        ? formatHourMinute(session.duration_minutes)
        : null;

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 12,
                color: 'var(--color-text-primary)',
                flexWrap: 'wrap',
            }}
        >
            <span
                className="tnum"
                style={{
                    minWidth: 110,
                    color: 'var(--color-text-secondary)',
                }}
            >
                {inTime} → {outTime || <em style={{ color: 'var(--color-status-today-deep)' }}>Still open</em>}
            </span>
            <span className="tnum" style={{ color: 'var(--color-text-primary)', minWidth: 60 }}>
                {duration || '—'}
            </span>
            {session.is_late_check_in && (
                <span className="pill pill-pending">
                    Late {session.late_minutes}m
                </span>
            )}
            {session.late_check_in_reason && (
                <span style={{ color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>
                    {session.late_check_in_reason}
                </span>
            )}
            {session.was_auto_closed && (
                <span className="pill pill-rejected">
                    Auto-closed · {session.auto_close_reason || 'unknown'}
                </span>
            )}
        </div>
    );
}

function PillBadge({ kind, children }) {
    const styles = {
        weekend:           { bg: 'var(--color-pill-cancelled-bg)', fg: 'var(--color-pill-cancelled-fg)' },
        holiday:           { bg: 'var(--color-pill-cancelled-bg)', fg: 'var(--color-pill-cancelled-fg)' },
        on_leave:          { bg: 'rgba(83, 74, 183, 0.12)',        fg: 'var(--color-status-on-leave)' },
        half_day_with_work:{ bg: 'rgba(83, 74, 183, 0.12)',        fg: 'var(--color-status-on-leave)' },
        in_progress:       { bg: 'var(--color-bg-info)',           fg: 'var(--color-status-today-deep)' },
        complete:          { bg: 'var(--color-pill-approved-bg)',  fg: 'var(--color-pill-approved-fg)' },
        incomplete:        { bg: 'var(--color-pill-pending-bg)',   fg: 'var(--color-pill-pending-fg)' },
        absent:            { bg: 'var(--color-pill-rejected-bg)',  fg: 'var(--color-pill-rejected-fg)' },
        today_no_data:     { bg: 'var(--color-bg-info)',           fg: 'var(--color-status-today-deep)' },
    };
    const s = styles[kind] || styles.weekend;
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 10px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 500,
                background: s.bg,
                color: s.fg,
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </span>
    );
}

function capitalize(s) {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------------------
// Department access editor — only rendered when employee.role === 'manager'.
// Backed by PUT /admin/employees/:id/manager-departments (set-the-whole-array)
// and the GET /admin/departments autocomplete source.
// ---------------------------------------------------------------------------

function DepartmentAccessSection({ employeeId, initialDepartments, onSaved }) {
    const [departments, setDepartments] = useState(() => [...initialDepartments].sort());
    const [adding, setAdding] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [flash, setFlash] = useState(null);

    const allDeptsQuery = useQuery({
        queryKey: ['admin', 'departments'],
        queryFn: () => adminApi.listDepartments(),
        staleTime: 5 * 60 * 1000,
    });
    const knownDepts = allDeptsQuery.data?.departments || [];

    const initialSet = useMemo(() => new Set(initialDepartments), [initialDepartments]);
    const currentSet = useMemo(() => new Set(departments), [departments]);
    const dirty = useMemo(() => {
        if (initialSet.size !== currentSet.size) return true;
        for (const d of currentSet) if (!initialSet.has(d)) return true;
        return false;
    }, [initialSet, currentSet]);

    const suggestions = useMemo(() => {
        const q = adding.trim().toLowerCase();
        if (q.length === 0) return [];
        return knownDepts
            .filter((d) => d.toLowerCase().includes(q))
            .filter((d) => !currentSet.has(d))
            .slice(0, 6);
    }, [adding, knownDepts, currentSet]);

    const addDepartment = (raw) => {
        const trimmed = (raw ?? adding).trim();
        if (trimmed.length === 0) return;
        if (currentSet.has(trimmed)) return;
        // If it's not in the known list, warn lightly (but allow). lessons-
        // learned flags this as the typo footgun.
        if (knownDepts.length > 0 && !knownDepts.includes(trimmed)) {
            const ok = window.confirm(
                `"${trimmed}" doesn't match any existing department.\n\n` +
                `Add it anyway? Make sure it matches the spelling used on employee profiles ` +
                `or the grant won't actually cover anyone.`
            );
            if (!ok) return;
        }
        setDepartments([...departments, trimmed].sort());
        setAdding('');
    };

    const removeDepartment = (dept) => {
        setDepartments(departments.filter((d) => d !== dept));
    };

    const save = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const res = await adminApi.setManagerDepartments(employeeId, departments);
            setFlash({
                tone: 'success',
                text: res.changed
                    ? 'Department access updated.'
                    : 'No changes to save.',
            });
            if (onSaved) onSaved();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Could not save.');
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setDepartments([...initialDepartments].sort());
        setAdding('');
        setError(null);
    };

    return (
        <div className="surface-card" style={{ padding: 18, marginBottom: 18 }}>
            <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, color: 'var(--color-text-secondary)' }}>
                Department access
            </h2>
            <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 0, marginBottom: 14, lineHeight: 1.5 }}>
                Departments this manager can view. Employees in any of these departments
                show up in their Live Presence list, attendance reports, and screenshot views.
            </p>

            {departments.length === 0 ? (
                <p style={{
                    fontSize: 13, color: 'var(--color-text-tertiary)', fontStyle: 'italic',
                    background: 'var(--color-bg-secondary)',
                    padding: '10px 12px', borderRadius: 'var(--border-radius-md)',
                    border: '0.5px solid var(--color-border-tertiary)',
                    marginBottom: 12,
                }}>
                    No departments granted — this manager currently has no visibility into any team.
                </p>
            ) : (
                <div className="flex items-center" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                    {departments.map((d) => (
                        <DepartmentPill key={d} label={d} onRemove={() => removeDepartment(d)} />
                    ))}
                </div>
            )}

            <div style={{ position: 'relative' }}>
                <div className="flex items-center" style={{ gap: 8 }}>
                    <input
                        type="text"
                        className="input"
                        placeholder="Add a department…"
                        list="admin-known-departments"
                        value={adding}
                        onChange={(e) => setAdding(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); addDepartment(); }
                        }}
                        style={{ flex: 1 }}
                    />
                    <datalist id="admin-known-departments">
                        {knownDepts
                            .filter((d) => !currentSet.has(d))
                            .map((d) => <option key={d} value={d} />)}
                    </datalist>
                    <button
                        type="button"
                        onClick={() => addDepartment()}
                        disabled={adding.trim().length === 0}
                        className="btn btn-soft"
                    >
                        Add
                    </button>
                </div>
                {suggestions.length > 0 && (
                    <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0,
                        marginTop: 4, background: 'var(--color-bg-primary)',
                        border: '0.5px solid var(--color-border-tertiary)',
                        borderRadius: 'var(--border-radius-md)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                        zIndex: 5,
                    }}>
                        {suggestions.map((d) => (
                            <button
                                key={d}
                                onClick={() => addDepartment(d)}
                                style={{
                                    display: 'block', width: '100%', textAlign: 'left',
                                    padding: '8px 12px', fontSize: 13,
                                    border: 0, background: 'transparent', cursor: 'pointer',
                                }}
                            >
                                {d}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {error && (
                <p style={{
                    fontSize: 13, color: 'var(--color-danger)',
                    background: 'var(--color-pill-rejected-bg)',
                    border: '0.5px solid var(--color-danger-border)',
                    borderRadius: 'var(--border-radius-md)',
                    padding: '8px 12px', marginTop: 12, marginBottom: 0,
                }}>
                    {error}
                </p>
            )}

            {flash && !dirty && (
                <p style={{
                    fontSize: 12, color: 'var(--color-pill-approved-fg)',
                    marginTop: 12, marginBottom: 0,
                }}>
                    {flash.text}
                </p>
            )}

            <div className="flex justify-end" style={{ gap: 8, marginTop: 14, paddingTop: 12, borderTop: '0.5px solid var(--color-border-tertiary)' }}>
                <button
                    onClick={reset}
                    disabled={!dirty || submitting}
                    className="btn btn-ghost"
                >
                    Reset
                </button>
                <button
                    onClick={save}
                    disabled={!dirty || submitting}
                    className="btn btn-primary"
                >
                    {submitting ? 'Saving…' : 'Save changes'}
                </button>
            </div>
        </div>
    );
}

function DepartmentPill({ label, onRemove }) {
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 6px 4px 10px',
                background: 'var(--color-bg-info)',
                color: 'var(--color-text-info)',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 500,
                border: '0.5px solid var(--color-border-info)',
            }}
        >
            {label}
            <button
                onClick={onRemove}
                aria-label={`Remove ${label}`}
                style={{
                    width: 18, height: 18, lineHeight: '16px', textAlign: 'center',
                    border: 0, background: 'transparent', color: 'inherit',
                    fontSize: 14, cursor: 'pointer', padding: 0, borderRadius: '50%',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
                ×
            </button>
        </span>
    );
}

// ---------------------------------------------------------------------------
// Screenshots section — inline grid of recent screenshots, with lightbox.
// Uses /api/v1/admin/employees/:id/screenshots.
// ---------------------------------------------------------------------------

const SHOT_RANGES = [
    { key: 'today',      label: 'Today' },
    { key: 'yesterday',  label: 'Yesterday' },
    { key: 'last_7',     label: 'Last 7 days' },
    { key: 'last_30',    label: 'Last 30 days' },
];

function computeShotRange(preset) {
    const today = DateTime.now().setZone('Asia/Karachi').startOf('day');
    if (preset === 'today') {
        return { start: today.toISODate(), end: today.toISODate() };
    }
    if (preset === 'yesterday') {
        const y = today.minus({ days: 1 });
        return { start: y.toISODate(), end: y.toISODate() };
    }
    if (preset === 'last_7') {
        return { start: today.minus({ days: 6 }).toISODate(), end: today.toISODate() };
    }
    if (preset === 'last_30') {
        return { start: today.minus({ days: 29 }).toISODate(), end: today.toISODate() };
    }
    return { start: today.toISODate(), end: today.toISODate() };
}

function ScreenshotsSection({ employeeId }) {
    const [preset, setPreset] = useState('today');
    const [lightbox, setLightbox] = useState({ open: false, flatIndex: 0 });

    const { start, end } = useMemo(() => computeShotRange(preset), [preset]);

    const query = useQuery({
        queryKey: ['admin', 'employee', employeeId, 'screenshots', start, end],
        queryFn: () => adminApi.getEmployeeScreenshots(employeeId, { start, end }),
        enabled: !!start && !!end,
        // Refetch every 60s so newly captured screenshots show up while the
        // page is open. The agent only writes ~6/hour so polling is cheap.
        refetchInterval: 60_000,
    });

    // Flatten day-grouped screenshots into a single list for the lightbox.
    const flat = useMemo(() => {
        if (!query.data?.days) return [];
        return query.data.days.flatMap((d) => d.screenshots);
    }, [query.data]);

    const openAt = (id) => {
        const idx = flat.findIndex((s) => s.id === id);
        if (idx >= 0) setLightbox({ open: true, flatIndex: idx });
    };

    return (
        <div className="surface-card" style={{ padding: 18, marginBottom: 18 }}>
            <h2 style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: 'var(--color-text-secondary)' }}>
                Screenshots
            </h2>

            <div className="flex items-center" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {SHOT_RANGES.map((p) => (
                    <RangeChip
                        key={p.key}
                        active={preset === p.key}
                        onClick={() => setPreset(p.key)}
                    >
                        {p.label}
                    </RangeChip>
                ))}
                {query.isSuccess && (
                    <span style={{
                        marginLeft: 8,
                        fontSize: 12,
                        color: 'var(--color-text-tertiary)',
                    }}>
                        {query.data.total} total
                    </span>
                )}
            </div>

            {query.isLoading && (
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Loading…</p>
            )}
            {query.isError && (
                <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>
                    Could not load screenshots.
                </p>
            )}
            {query.isSuccess && (!query.data?.days || query.data.days.length === 0) && (
                <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                    No screenshots in this range. The desktop agent captures while the employee is checked in.
                </p>
            )}

            {query.isSuccess && query.data?.days?.length > 0 && (
                <div className="flex flex-col" style={{ gap: 14 }}>
                    {query.data.days.map((d) => (
                        <ScreenshotDay key={d.date} day={d} onOpen={openAt} />
                    ))}
                </div>
            )}

            <ScreenshotLightbox
                open={lightbox.open}
                screenshots={flat}
                currentIndex={lightbox.flatIndex}
                onClose={() => setLightbox({ open: false, flatIndex: 0 })}
                onNavigate={(i) => setLightbox({ open: true, flatIndex: i })}
            />
        </div>
    );
}

function ScreenshotDay({ day, onOpen }) {
    const dt = DateTime.fromISO(day.date);
    const heading = `${dt.toFormat('EEEE')}, ${dt.toFormat('d LLL')}`;
    return (
        <div>
            <div
                style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 8,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                }}
            >
                {heading} · {day.screenshots.length} screenshot{day.screenshots.length !== 1 ? 's' : ''}
            </div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: 8,
                }}
            >
                {day.screenshots.map((s) => (
                    <ScreenshotThumb key={s.id} shot={s} onOpen={onOpen} />
                ))}
            </div>
        </div>
    );
}

function ScreenshotThumb({ shot, onOpen }) {
    const dt = DateTime.fromISO(shot.timestamp).setZone('Asia/Karachi');
    return (
        <button
            onClick={() => onOpen(shot.id)}
            style={{
                position: 'relative',
                background: 'var(--color-bg-secondary)',
                border: '0.5px solid var(--color-border-tertiary)',
                borderRadius: 'var(--border-radius-md)',
                padding: 0,
                cursor: 'pointer',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
            }}
            title={`${shot.active_app || '(no app)'} · ${dt.toFormat('HH:mm:ss')}`}
        >
            <div style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '16 / 9',
                background: '#000',
            }}>
                <AuthenticatedImage
                    screenshotId={shot.id}
                    alt={shot.active_app}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
                {!shot.viewed_by_me && (
                    <span
                        title="Not yet viewed"
                        style={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: 'var(--color-status-today)',
                            boxShadow: '0 0 0 2px rgba(255,255,255,0.6)',
                        }}
                    />
                )}
            </div>
            <div style={{ padding: '6px 8px', fontSize: 11 }}>
                <div className="tnum" style={{
                    color: 'var(--color-text-primary)',
                    fontWeight: 500,
                }}>
                    {dt.toFormat('HH:mm:ss')}
                </div>
                <div style={{
                    color: 'var(--color-text-tertiary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {shot.active_app || '—'}
                </div>
            </div>
        </button>
    );
}
